import { axiosInstance, API_BASE_URL } from "../config/axiosInstance";

export interface GetRequestListByRdmIdsRequestDto {
  rdmIds: number[];
  orId: number;
}

// Request Types with Privileges Response DTO
export interface RequestTypeWithPrivilegeDto {
  privid: number;
  name: string;
  objectid: number;
  linkedobjectid?: number;
  ordernumber?: number;
  uorfid?: number;
  subtypes: RequestSubtypeDto[];
}

export interface RequestSubtypeDto {
  ids: number[];
  name: string;
}

export interface GetRequestTypesWithPrivilegesResponseDto {
  data: RequestTypeWithPrivilegeDto[];
  isSuccess: boolean;
  statusCode: number;
  message: string;
}

// Favorites Types
export interface AddToFavoritesRequestDto {
  RD_ID: number;
}

export interface GetFavoritesResponseDto {
  id: number;
  rd_id: number;
  process_name?: string;
  order_number: number;
  or_id: number;
  process_id: number;
  name: string;
  active: string;
  parent_id?: number;
  note?: string;
}

export interface ServiceAddFavoriteResponseDto {
  data: any;
  issuccess: boolean;
  statuscode: number;
  message: string;
}

// Test Times Types
export interface GetTestTimesRequestDto {
  rd_ids: number[];
  off_id: number;
}

export interface GetTestTimesResponseDto {
  process_id: number;
  running_time: string;
}

// Service Prices Types
export interface FindServicePricesMultiRequestDto {
  ptid: number;
  services: Array<{
    serviceid: number;
    quantity: number;
  }>;
}

export interface ServicePriceDetailDto {
  id: number;
  name: string;
  code: string;
  quantity: number;
  taxratio: number;
  pattotalamount: number;
  antotalamount: number;
  total: number;
}

export interface ServicePriceLineDto {
  id: number;
  price: number;
  totalprice: number;
  taxtotal: number;
  nettotal: number;
  associationid: number;
}

export interface ServicePriceResultDto {
  serviceid: number;
  success: boolean;
  errormessage: string;
  servicedetail: ServicePriceDetailDto;
  pricelines: ServicePriceLineDto[];
}

export interface FindServicePricesMultiResponseDto {
  data: {
    success: boolean;
    errormessage: string;
    errornumber: number;
    serviceresults: ServicePriceResultDto[];
  };
  issuccess: boolean;
  statuscode: number;
  message: string;
}

export interface FindServicePricesSimpleRequestDto {
  ptid: number;
  serviceid: number;
  quantity: number;
}

export interface FindServicePricesSimpleResponseDto {
  data: {
    success: boolean;
    servicedetail: ServicePriceDetailDto;
    pricelines?: ServicePriceLineDto[];
  };
  issuccess: boolean;
  statuscode: number;
  message: string;
}

export interface GetRequestListByRdmIdResponseDto {
  ORDER_NUMBER: number;
  ID: number;
  OR_ID: number;
  PROCESS_ID: number;
  PROCESS_NAME: string;
  ACTIVE: string;
  PARENT_ID?: number;
  NAME: string;
  NOTE?: string;
}

// Patient Request History Types
export interface GetPatientRequestHistoryRequestDto {
  ptId: number;
  timedays: number;
}

export interface PatientRequestHistoryItemDto {
  id: number;
  or_id: number;
  process_id: number;
  process_name: string;
  kart_adi: string;
  active: string;
  parent_id?: number;
  order_number?: number;
  note?: string;
  dates: string[];
}

export interface GetPatientRequestHistoryResponseDto {
  data: PatientRequestHistoryItemDto[];
  issuccess: boolean;
  statuscode: number;
  message: string;
}

// Create Patient Request Types
// AddNewInfo DTOs (ek bilgi formları)
export interface ContrastFormDto {
  IsContrasted?: string | null;
}

export interface ExaminationRequestFormDto {
  ExaminationNote?: string | null;
}

export interface HeightInfoFormDto {
  Height?: number | null;
  Weight?: number | null;
  VKI?: number | null;
  PsychologicalStatusId?: number | null;
  MarriageId?: number | null;
}

export interface ConsultationFormDto {
  CrNote?: string | null;
  CtDoctorId?: number | null;
  CtDepartmentId?: number | null;
  CtRefDoctorId?: number | null;
  CtCalledTime?: string | null; // ISO DateTime
  CtPatientTypeId?: number | null;
}

export interface HIVFormDto {
  HivInfoId?: number | null;
}

export interface LabRequestFormDto {
  LabSampleTakenSiteId?: number | null;
}

export interface PathologyFormDto {
  GDate?: string | null; // ISO DateTime
  TissueSamplesId?: number | null;
  SamplingMethodId?: number | null;
  LocationId?: number | null;
  PTime?: string | null; // ISO DateTime
  Tissue?: string | null;
  PUserName?: string | null;
  SampleType?: number | null;
}

export interface EGFRFormDto {
  Height?: number | null;
  PatientMuscleFactor?: number | null;
}

export interface MedicalGeneticsFormDto {
  Note?: string | null;
}

export interface ProcessDirectionFormDto {
  OlrId?: number | null;
}

export interface PregnantControlFormDto {
  PregnantOrLactation?: string | null;
}

export interface AskDoctorFormDto {
  PatientTypeId?: number | null;
  DeptId?: number | null;
  DoctorId?: number | null;
  RefDoctorId?: number | null;
  AssistantDoctorId?: number | null;
  RefPersonelId?: number | null;
  BedId?: number | null;
}

export interface PacemakerControlFormDto {
  PacemakerControl?: string | null;
}

export interface AddNewInfoDto {
  CONTRAST?: ContrastFormDto | null;
  EXAMINATION_REQUEST?: ExaminationRequestFormDto | null;
  HEIGHT_INFO?: HeightInfoFormDto | null;
  CONSULTATION?: ConsultationFormDto | null;
  HIV?: HIVFormDto | null;
  LAB_REQUEST?: LabRequestFormDto | null;
  PATHOLOGY?: PathologyFormDto | null;
  EGFR?: EGFRFormDto | null;
  MEDICAL_GENETICS?: MedicalGeneticsFormDto | null;
  PROCESS_DIRECTION?: ProcessDirectionFormDto | null;
  PREGNANT_CONTROL?: PregnantControlFormDto | null;
  ASK_DOCTOR?: AskDoctorFormDto | null;
  ASK_DOCTOR_REQUEST?: AskDoctorFormDto | null;
  PACEMAKER_CONTROL?: PacemakerControlFormDto | null;
}

export interface CreatePatientRequestServiceDto {
  processId: number;
  orId: number;
  quantity: number;
  note: string;
  addnewinfo?: AddNewInfoDto | null;
}

export interface CreatePatientRequestRequestDto {
  ptId: number;
  note: string;
  pTime: string;
  isPregnant: string;
  urgent: string;
  services: CreatePatientRequestServiceDto[];
  addnewinfo?: AddNewInfoDto | null;
}

export interface CreatePatientRequestResponseDto {
  data: any;
  issuccess: boolean;
  statuscode: number;
  message: string;
}

// New CreatePatientRequest DTOs (for /reference/api/Request/CreatePatientRequest)
export interface CreatePatientRequestDetailRequestDto {
  ProcessId: number;
  Quantity: number;
  Note?: string | null; // Service note - optional
  OlrId?: number; // Yön - optional
  addnewinfo?: AddNewInfoDto | null;
}

export interface CreatePatientRequestNewRequestDto {
  PtId: number;
  Note?: string | null; // Request note
  PTime?: string | null; // Procedure time - ISO DateTime string
  IsPregnant?: string | null; // "1" or "0" or null
  Urgent?: string | null; // Urgency level as string
  Services: CreatePatientRequestDetailRequestDto[];
  addnewinfo?: AddNewInfoDto | null;
}

export interface CreatePatientRequestNewResponseDto {
  data: any;
  issuccess: boolean;
  statuscode: number;
  message: string;
}

export interface AddProcessToPatFileBulkItemDto {
  ServiceId: number;
  Quantity: number;
  OsId?: number | null;
  OlrId?: number | null;
  addnewinfo?: AddNewInfoDto | null;
}

export interface AddProcessToPatFileBulkRequestDto {
  PtId: number;
  Items: AddProcessToPatFileBulkItemDto[];
  addnewinfo?: AddNewInfoDto | null;
}

export interface AddProcessToPatFileBulkErrorDto {
  serviceid: number;
  quantity: number;
  errorid: number;
  errormessage: string;
}

export interface AddProcessToPatFileBulkResultDto {
  serviceid: number;
  quantity: number;
  message?: string;
}

export interface AddProcessToPatFileBulkDataDto {
  success: boolean;
  results: AddProcessToPatFileBulkResultDto[];
  errors: AddProcessToPatFileBulkErrorDto[];
}

export interface AddProcessToPatFileBulkResponseDto {
  data: AddProcessToPatFileBulkDataDto;
  issuccess: boolean;
  statuscode: number;
  message: string;
}

// Select Service Simple Types
export interface SelectServiceSimpleRequestDto {
  ptid: number;
  processid: number;
  officeid: number;
  isselected: boolean;
  quantity: number;
  note: string;
}

export interface ValidationResult {
  isvalid: boolean;
  errors: string[];
  warnings: string[];
  alerts: string[];
  hasexistinglabtest: boolean;
  requiresadditionalinfo: boolean;
  requirespatfilemodify: boolean;
  requestcategorytype: number;
  processcontrolresult: any;
  hascategorycontrol: boolean;
  processcategory: string;
  // Backend'den gelen willopencard listesi burada dönüyor
  willopencard?: WillOpenCardItemDto[];
}

export type WillOpenCardKey =
  | "HEIGHT_INFO"
  | "HEIGHT_INFO_REQUEST"
  | "PROCESS_DIRECTION"
  | "PROCESS_DIRECTION_REQUEST"
  | "ASK_DOCTOR"
  | "ASK_DOCTOR_REQUEST"
  | "PREGNANT_CONTROL"
  | "PREGNANT_CONTROL_REQUEST"
  | "HIV"
  | "HIV_REQUEST"
  | "PACEMAKER"
  | "PACEMAKER_CONTROL"
  | "PACEMAKER_CONTROL_REQUEST"
  | "CONSULTATION"
  | "CONSULTATION_REQUEST"
  | "LAB_REQUEST"
  | "PATHOLOGY"
  | "PATHOLOGY_REQUEST"
  | "PATIENT_FILE_CHANGE"
  | "CONTRAST"
  | "CONTRAST_REQUEST"
  | "EXAMINATION_REQUEST"
  | "VKI_ADD"
  | "VKI_REQUEST"
  | "EGFR"
  | "EGFR_REQUEST"
  | "MEDICAL_GENETICS_REQUEST";

export interface WillOpenCardItemDto {
  key: WillOpenCardKey;
  reason?: string;
  title?: string;
}

export interface SelectServiceSimpleResponseDto {
  issuccessful: boolean;
  errormessage: string | null;
  validationresult: ValidationResult;
  requiresadditionalinfo: boolean;
  requirespatfilemodify: boolean;
  additionalinfodata: any;
  patfilemodifydata: any;
  alerts: string[];
  selectedservice: any;
  requestid: number;
  requestdetailid: number;
  message: string;
  additionalinfoform: any;
  patfilemodifyform: any;
  addedservices: any[];
  willopencard?: WillOpenCardItemDto[];
}

// Additional Info Form Types
export interface AdditionalInfoFormRequestDto {
  serviceid: number;
  requestdetailid: number;
  // İleride backend desteği geldikçe kart bazlı ek bilgi için kullanılabilecek opsiyonel alanlar
  key?: WillOpenCardKey;
  ptId?: number;
  officeId?: number;
}

export interface AdditionalInfoFormResponseDto {
  data: any;
  isSuccess: boolean;
  statusCode: number;
  message: string;
}

// Confirm Selection Types
export interface ConfirmSelectionRequestDto {
  ptId: number;
  processId: number;
  isSelected: boolean;
  quantity?: number;
  officeId?: number;
}

export interface ConfirmSelectionResponseDto {
  data: any;
  isSuccess: boolean;
  statusCode: number;
  message: string;
}

// Service Categories Types
export interface ServiceCategoryDto {
  id: number;
  name: string;
  parent_id?: number | null;
  st_name?: string | null;
  sco_name?: string | null;
  child_categories: ServiceCategoryDto[];
}

export interface GetServiceCategoriesWithNamesResponseDto {
  data: ServiceCategoryDto[];
  isSuccess: boolean;
  statusCode: number;
  message: string;
}

// Get Add Services Types
export interface AddServiceDto {
  id: number;
  name: string;
  code: string;
  st_name: string | null;
  pct_name: string | null;
  ot_name: string | null;
  gpc_name: string | null;
  gpc_code: number | null;
  srt_name: string | null;
  rt_name: string | null;
  sc_name: string | null;
  pack_type: number;
  note: string | null;
  gss_send_type: number;
  huv_name: string | null;
  huv_code: string | null;
  huv_coef: string | null;
  or_id?: number;
}

export interface GetAddServicesResponseDto {
  data: AddServiceDto[];
  isSuccess: boolean;
  statusCode: number;
  message: string;
}

// Price List Period Types
export interface PriceListPeriodDto {
  id: number;
  name: string;
  coefficient: number | null;
  plid: number;
}

export interface GetPriceListPeriodsResponseDto {
  data: PriceListPeriodDto[];
  issuccess: boolean;
  statuscode: number;
  message: string;
}

// Operation Seances Types
export interface OperationSeanceDto {
  id: number;
  name: string;
  quantity: number;
  code: string;
  oss_code: string;
}

export interface GetOperationSeancesResponseDto {
  data: OperationSeanceDto[];
  issuccess: boolean;
  statuscode: number;
  message: string;
}

// Patient Process History Types (ServiceAdd)
export interface GetPatientProcessHistoryRequestDto {
  ptId: number;
}

export interface PatientProcessHistoryItemDto {
  process_id: number;
  row_type: string;
  process_name: string;
  sc_name: string;
  sc_id: number;
  dates: string[];
}

export interface GetPatientProcessHistoryResponseDto {
  data: PatientProcessHistoryItemDto[];
  issuccess: boolean;
  statuscode: number;
  message: string;
}

export interface SuccessResponse<T> {
  data: T;
  issuccess: boolean;
  statuscode: number;
  message: string;
}

export interface GetPatientRequestsResponseDto {
  id: number;
  pt_id: number;
  or_id: number;
  ps_id: number;
  note: string | null;
  ptime: string;
  ctime: string;
  process_ctime?: string;
  cuser: string;
  pat_req_order_number: number | null;
  pty_id: number;
  prot_id: string;
  upn: number;
  off_id: number;
  dept_name: string;
  dr_name: string;
  object_name: string; // request type name
  status_name: string;
  dr_id: number;
  dept_id: number;
  identity_no: string;
}

export interface GetPatientRequestDetailsResponseDto {
  id: number;
  prd_id?: number;
  pr_id?: number;
  process_id?: number;
  process_name: string;
  note: string | null;
  quantity: number;
  request_status?: string;
  status_name?: string;
  action_status?: string;
  process_status?: string;
  pacs_status?: string;
  consultation_note?: string;
  cr_note?: string;
  action_date?: string;
  ptime?: string;
  cuser?: string;
  performed_by?: string;
  request_date?: string;
  ctime?: string;
}

export interface GetPatientProcessOutsideRequestsResponseDto {
  id: number;
  ptd_id?: number;
  process_id?: number;
  process_name: string;
  note: string | null;
  quantity: number;
  request_status?: string;
  status_name?: string;
  action_status?: string;
  process_status?: string;
  pacs_status?: string;
  consultation_note?: string;
  cr_note?: string;
  action_date?: string;
  ptime?: string;
  cuser?: string;
  performed_by?: string;
  request_date?: string;
  ctime?: string;
}

export interface GetAllRequestsResponseDto {
  id: number;
  prd_id?: number;
  process_id?: number;
  process_name?: string;
  action_name?: string;
  action_date?: string;
  ptime?: string;
  note: string | null;
  quantity: number;
  pacs_status?: string;
  consultation_note?: string;
  cr_note?: string;
  dr_name?: string;
  dept_name?: string;
  request_status?: string;
  status_name?: string;
  request_date?: string;
  ctime?: string;
  object_name?: string;
  service_type?: string;
  pro_id?: number;
}

export interface GetAllVisitsRequestsResponseDto {
  id: number;
  prd_id?: number;
  process_id?: number;
  process_name?: string;
  action_name?: string;
  action_date?: string;
  ptime?: string;
  note: string | null;
  quantity: number;
  pacs_status?: string;
  consultation_note?: string;
  cr_note?: string;
  dr_name?: string;
  dept_name?: string;
  request_status?: string;
  status_name?: string;
  request_date?: string;
  ctime?: string;
  object_name?: string;
  service_type?: string;
  pro_id?: number;
}

export interface CancelRequestDto {
  requestId: number;
  cancelNote: string;
  cancelUser: string;
}

export interface CancelRequestDetailDto {
  prdId: number;
  cancelNote: string;
  cancelUser: string;
}

export interface RemoveFromPackageDto {
  prdId: number;
  ptId: number;
}
export interface UpdateRequestDetailNotesDto {
  prdId: number;
  note: string;
  crNote: string;
}

export type PackageAction = "MoveOut" | "DeleteAll" | "Cancel";

export interface CancelApprovedRequestDto {
  requestId: number;       // PATIENT_REQUEST.ID
  ptId: number;            // PATIENT_TRANSACTIONS.ID
  cancelNote: string;
  drId?: number | null;
  packageAction?: PackageAction | null;
}

export interface CancelApprovedRequestDetailDto {
  prdId: number;           // PATIENT_REQUEST_DETAIL.ID
  ptId: number;            // PATIENT_TRANSACTIONS.ID
  cancelNote: string;
  drId?: number | null;
  packageAction?: PackageAction | null;
}

export const requestService = {
  getRequestTypesWithPrivileges: async (): Promise<any> => {
    try {
      const response = await axiosInstance.get(
        `${API_BASE_URL}/reference/api/Request/GetRequestTypesWithPrivileges`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching request types with privileges:", error);
      throw error;
    }
  },
  
  getRequestListByRdmIds: async (request: GetRequestListByRdmIdsRequestDto): Promise<{ data: GetRequestListByRdmIdResponseDto[] }> => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/reference/api/Request/GetRequestListByRdmIds`,
        request
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching request list by rdmId:", error);
      throw error;
    }
  },

  // Favorites Services
  addToFavorites: async (rdId: number): Promise<{ data: boolean }> => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/reference/api/Request/AddToFavorites`,
        null,
        {
          params: {
            rdId: rdId
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error adding to favorites:", error);
      throw error;
    }
  },

  removeFromFavorites: async (id: number): Promise<{ data: boolean }> => {
    try {
      const response = await axiosInstance.delete(
        `${API_BASE_URL}/reference/api/Request/RemoveFromFavorites`,
        {
          params: {
            id: id
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error removing from favorites:", error);
      throw error;
    }
  },

  getFavorites: async (orId?: number): Promise<{ data: GetFavoritesResponseDto[] }> => {
    try {
      const params: any = {};
      if (orId !== undefined) {
        params.orId = orId;
      }
      
      const response = await axiosInstance.get(
        `${API_BASE_URL}/reference/api/Request/GetFavorites`,
        {
          params: params
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching favorites:", error);
      throw error;
    }
  },

  // Test Times Service
  getTestTimes: async (request: GetTestTimesRequestDto): Promise<{ data: GetTestTimesResponseDto[] }> => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/reference/api/Request/GetTestTimes`,
        request
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching test times:", error);
      throw error;
    }
  },

  // Service Prices Service
  findServicePricesMulti: async (request: FindServicePricesMultiRequestDto): Promise<FindServicePricesMultiResponseDto> => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/reference/api/Request/FindServicePricesMulti`,
        request
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching service prices:", error);
      throw error;
    }
  },

  findServicePricesSimple: async (request: FindServicePricesSimpleRequestDto): Promise<FindServicePricesSimpleResponseDto> => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/reference/api/Request/FindServicePricesSimple`,
        request
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching service price (simple):", error);
      throw error;
    }
  },

  // Patient Request History Service
  getPatientRequestHistory: async (request: GetPatientRequestHistoryRequestDto): Promise<GetPatientRequestHistoryResponseDto> => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/reference/api/Request/GetPatientRequestHistory`,
        request
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching patient request history:", error);
      throw error;
    }
  },

  // Create Patient Request Service
  createPatientRequest: async (request: CreatePatientRequestRequestDto): Promise<CreatePatientRequestResponseDto> => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/reference/api/Request/CreatePatientRequest`,
        request
      );
      return response.data;
    } catch (error) {
      console.error("Error creating patient request:", error);
      throw error;
    }
  },

  // Select Service Simple
  selectServiceSimple: async (request: SelectServiceSimpleRequestDto): Promise<SelectServiceSimpleResponseDto> => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/reference/api/ServiceSelection/select-service-simple`,
        request
      );
      return response.data;
    } catch (error) {
      console.error("Error selecting service:", error);
      throw error;
    }
  },

  // Get Additional Info Form
  getAdditionalInfoForm: async (request: AdditionalInfoFormRequestDto): Promise<AdditionalInfoFormResponseDto> => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/reference/api/ServiceSelection/additional-info/form`,
        request
      );
      return response.data;
    } catch (error) {
      console.error("Error getting additional info form:", error);
      throw error;
    }
  },

  // WillOpenCard KEY bazlı merkezi AdditionalInfo fetch
  getAdditionalInfoForKey: async (params: {
    key: WillOpenCardKey;
    serviceId: number;
    requestDetailId: number;
    ptId?: number;
    officeId?: number;
  }): Promise<AdditionalInfoFormResponseDto> => {
    const { key, serviceId, requestDetailId, ptId, officeId } = params;

    const payload: AdditionalInfoFormRequestDto = {
      serviceid: serviceId,
      requestdetailid: requestDetailId,
      key,
      ...(ptId !== undefined ? { ptId } : {}),
      ...(officeId !== undefined ? { officeId } : {}),
    };

    return requestService.getAdditionalInfoForm(payload);
  },

  // Confirm Selection
  confirmSelection: async (request: ConfirmSelectionRequestDto): Promise<ConfirmSelectionResponseDto> => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/reference/api/ServiceSelection/confirm-selection`,
        request
      );
      return response.data;
    } catch (error) {
      console.error("Error confirming selection:", error);
      throw error;
    }
  },

  getPriceListPeriods: async (): Promise<GetPriceListPeriodsResponseDto> => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/reference/api/PriceListPeriod/GetPriceListPeriods`,
        {}
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching price list periods:", error);
      throw error;
    }
  },

  getServiceCategoriesWithNames: async (): Promise<GetServiceCategoriesWithNamesResponseDto> => {
    try {
      const response = await axiosInstance.get(
        `${API_BASE_URL}/reference/api/ServiceAdd/GetServiceCategoriesWithNames`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching service categories:", error);
      throw error;
    }
  },

  getAddServices: async (scId: number, isHuv: boolean, plpId?: number): Promise<GetAddServicesResponseDto> => {
    try {
      const params: any = {
        scId: scId,
        isHuv: isHuv,
      };

      if (plpId) {
        params.plpId = plpId;
      }

      const response = await axiosInstance.get(
        `${API_BASE_URL}/reference/api/ServiceAdd/GetAddServices`,
        {
          params: params
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching add services:", error);
      throw error;
    }
  },

  getAddServiceFavorites: async (isHuv: boolean, plpId?: number): Promise<GetAddServicesResponseDto> => {
    try {
      const params: any = {
        isHuv: isHuv
      };

      if (plpId) {
        params.plpId = plpId;
      }

      const response = await axiosInstance.get(
        `${API_BASE_URL}/reference/api/ServiceAdd/GetAddServiceFavorites`,
        {
          params: params
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching favorite services:", error);
      throw error;
    }
  },

  addServiceFavorite: async (serviceId: number): Promise<ServiceAddFavoriteResponseDto> => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/reference/api/ServiceAdd/ServiceAddFavoriteAdd`,
        null,
        {
          params: {
            serviceId,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error adding service favorite:", error);
      throw error;
    }
  },

  removeServiceFavorite: async (serviceId: number): Promise<ServiceAddFavoriteResponseDto> => {
    try {
      const response = await axiosInstance.delete(
        `${API_BASE_URL}/reference/api/ServiceAdd/ServiceAddFavoriteRemove`,
        {
          params: {
            serviceId: serviceId
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error removing service favorite:", error);
      throw error;
    }
  },

  getOperationSeances: async (): Promise<GetOperationSeancesResponseDto> => {
    try {
      const response = await axiosInstance.get(
        `${API_BASE_URL}/reference/api/ServiceAdd/GetOperationSeances`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching operation seances:", error);
      throw error;
    }
  },

  getPatientProcessHistory: async (request: GetPatientProcessHistoryRequestDto): Promise<GetPatientProcessHistoryResponseDto> => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/reference/api/ServiceAdd/GetPatientProcessHistory`,
        request
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching patient process history:", error);
      throw error;
    }
  },

  // Get Process Control Price
  getProcessControlPrice: async (params: { code: number; offID: number }): Promise<any> => {
    try {
      const response = await axiosInstance.get(
        `${API_BASE_URL}/reference/api/ProcessControlPrice/GetProcessControlPrice`,
        { params }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching process control price:", error);
      throw error;
    }
  },

  // Create Patient Request (new endpoint)
  createPatientRequestNew: async (request: CreatePatientRequestNewRequestDto): Promise<CreatePatientRequestNewResponseDto> => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/reference/api/Request/CreatePatientRequest`,
        request
      );
      return response.data;
    } catch (error) {
      console.error("Error creating patient request:", error);
      throw error;
    }
  },

  addProcessToPatFileBulk: async (
    request: AddProcessToPatFileBulkRequestDto
  ): Promise<AddProcessToPatFileBulkResponseDto> => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/reference/api/Request/AddProcessToPatFileBulk`,
        request
      );
      return response.data;
    } catch (error) {
      console.error("Error adding process to patient file in bulk:", error);
      throw error;
    }
  },

  // GET: Hasta taleplerini listeler
  getPatientRequests: async (
    patientId: number
  ): Promise<SuccessResponse<GetPatientRequestsResponseDto[]>> => {
    try {
      const response = await axiosInstance.get(
        `${API_BASE_URL}/reference/api/ServiceRequestedList/GetPatientRequests`,
        { params: { patientId } }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching patient requests:", error);
      throw error;
    }
  },

  // GET: Talep detaylarını listeler
  getPatientRequestDetails: async (
    requestId: number
  ): Promise<SuccessResponse<GetPatientRequestDetailsResponseDto[]>> => {
    try {
      const response = await axiosInstance.get(
        `${API_BASE_URL}/reference/api/ServiceRequestedList/GetPatientRequestDetails`,
        { params: { requestId } }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching patient request details:", error);
      throw error;
    }
  },

  // GET: Dış işlem taleplerini listeler
  getPatientProcessOutsideRequests: async (
    patientId: number
  ): Promise<SuccessResponse<GetPatientProcessOutsideRequestsResponseDto[]>> => {
    try {
      const response = await axiosInstance.get(
        `${API_BASE_URL}/reference/api/ServiceRequestedList/GetPatientProcessOutsideRequests`,
        { params: { patientId } }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching patient process outside requests:", error);
      throw error;
    }
  },

  // GET: Bekleyen talep detaylarını listeler
  getPendingRequestDetails: async (
    upn: number,
    psId: number
  ): Promise<SuccessResponse<GetPatientRequestDetailsResponseDto[]>> => {
    try {
      const response = await axiosInstance.get(
        `${API_BASE_URL}/reference/api/ServiceRequestedList/GetPendingRequestDetails`,
        { params: { upn, psId } }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching pending request details:", error);
      throw error;
    }
  },

  // GET: Hastanın tüm taleplerini listeler
  getAllServiceRequests: async (
    patientId: number
  ): Promise<SuccessResponse<GetAllRequestsResponseDto[]>> => {
    try {
      const response = await axiosInstance.get(
        `${API_BASE_URL}/reference/api/ServiceRequestedList/GetAllRequests`,
        { params: { patientId } }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching all requests:", error);
      throw error;
    }
  },

  // GET: Tüm ziyaret taleplerini listeler
  getAllVisitsRequests: async (
    upn: number,
    days?: number
  ): Promise<SuccessResponse<GetAllVisitsRequestsResponseDto[]>> => {
    try {
      const params: { upn: number; days?: number } = { upn };
      if (days !== undefined) {
        params.days = days;
      }
      const response = await axiosInstance.get(
        `${API_BASE_URL}/reference/api/ServiceRequestedList/GetAllVisitsRequests`,
        { params }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching all visits requests:", error);
      throw error;
    }
  },

  // GET: Ziyaret talep detaylarını listeler
  getAllVisitsRequestDetails: async (
    requestId: number
  ): Promise<SuccessResponse<GetPatientRequestDetailsResponseDto[]>> => {
    try {
      const response = await axiosInstance.get(
        `${API_BASE_URL}/reference/api/ServiceRequestedList/GetAllVisitsRequestDetails`,
        { params: { requestId } }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching all visits request details:", error);
      throw error;
    }
  },

  // GET: Kullanıcının tüm istekleri iptal etme yetkisi var mı?
  hasCancelAllReqsPrivilege: async (): Promise<SuccessResponse<boolean>> => {
    try {
      const response = await axiosInstance.get(
        `${API_BASE_URL}/reference/api/ServiceRequestedList/HasCancelAllReqsPrivilege`
      );
      return response.data;
    } catch (error) {
      console.error("Error checking cancel all requests privilege:", error);
      throw error;
    }
  },

  // POST: Bekleyen isteği tamamen iptal eder
  cancelRequest: async (
    dto: CancelRequestDto
  ): Promise<SuccessResponse<boolean>> => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/reference/api/ServiceRequestedList/CancelRequest`,
        dto
      );
      return response.data;
    } catch (error) {
      console.error("Error cancelling request:", error);
      throw error;
    }
  },

  // POST: Bekleyen tek bir detayı iptal eder
  cancelRequestDetail: async (
    dto: CancelRequestDetailDto
  ): Promise<SuccessResponse<boolean>> => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/reference/api/ServiceRequestedList/CancelRequestDetail`,
        dto
      );
      return response.data;
    } catch (error) {
      console.error("Error cancelling request detail:", error);
      throw error;
    }
  },

  // POST: İşlemi paketten çıkarır
  removeFromPackage: async (
    dto: RemoveFromPackageDto
  ): Promise<SuccessResponse<boolean>> => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/reference/api/ServiceRequestedList/RemoveFromPackage`,
        dto
      );
      return response.data;
    } catch (error) {
      console.error("Error removing from package:", error);
      throw error;
    }
  },

  // POST: İstek detay notlarını günceller
  updateRequestDetailNotes: async (
    dto: UpdateRequestDetailNotesDto
  ): Promise<SuccessResponse<boolean>> => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/reference/api/ServiceRequestedList/UpdateRequestDetailNotes`,
        dto
      );
      return response.data;
    } catch (error) {
      console.error("Error updating request detail notes:", error);
      throw error;
    }
  },

  // POST: Onaylanmış isteği iptal eder
  cancelApprovedRequest: async (
    dto: CancelApprovedRequestDto
  ): Promise<SuccessResponse<boolean>> => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/reference/api/ServiceRequestedList/CancelApprovedRequest`,
        dto
      );
      return response.data;
    } catch (error) {
      console.error("Error cancelling approved request:", error);
      throw error;
    }
  },

  // POST: Onaylanmış tek bir detayı iptal eder
  cancelApprovedRequestDetail: async (
    dto: CancelApprovedRequestDetailDto
  ): Promise<SuccessResponse<boolean>> => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/reference/api/ServiceRequestedList/CancelApprovedRequestDetail`,
        dto
      );
      return response.data;
    } catch (error) {
      console.error("Error cancelling approved request detail:", error);
      throw error;
    }
  },
};
